// src/index.ts
import axios from "axios";
var createAuthRequestInterceptor = (options) => {
  const { getAuthToken, captureException } = options || {};
  return {
    onFulfilled: async (config) => {
      try {
        if (getAuthToken) {
          const token = await getAuthToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      } catch (error) {
        const isDev = typeof process !== "undefined" ? process.env.NODE_ENV === "development" : false;
        if (captureException && !isDev) {
          captureException(error instanceof Error ? error : String(error), {
            data: {
              message: "Failed to retrieve auth token",
              originalError: error
            }
          });
        }
        return config;
      }
    },
    onRejected: (error) => {
      if (captureException) {
        captureException(error, {
          data: {
            message: "Request interceptor error",
            originalError: error
          }
        });
      }
      return Promise.reject(error);
    }
  };
};
var create401ResponseInterceptor = (options) => {
  const { logoutAction } = options || {};
  return {
    onFulfilled: (response) => response,
    onRejected: (error) => {
      var _a, _b, _c;
      if (((_a = error.response) == null ? void 0 : _a.status) === 401) {
        const errorData = error.response.data;
        const errorType = (
          // Using `any` here is justified because error response shapes vary across backends/proxies.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ((_b = errorData == null ? void 0 : errorData.body) == null ? void 0 : _b.errorType) || (errorData == null ? void 0 : errorData.errorType) || ((_c = errorData == null ? void 0 : errorData.error) == null ? void 0 : _c.errorType)
        );
        if (errorType === "authentication") {
          void (logoutAction == null ? void 0 : logoutAction());
        }
      }
      return Promise.reject(error);
    }
  };
};
var createErrorResponseInterceptor = (options) => {
  const { captureException } = options || {};
  return {
    onFulfilled: (response) => response,
    onRejected: (error) => {
      var _a, _b;
      if (error.message === "Network Error") {
        if (captureException) {
          captureException("CORS error occurred", {
            data: {
              message: "Network Error",
              originalError: error
            }
          });
        }
        if (error.config) {
          console.debug("Failed request details:", {
            url: error.config.url,
            method: error.config.method,
            headers: error.config.headers
          });
        }
      } else if (((_a = error.response) == null ? void 0 : _a.status) && error.response.status >= 400 && error.response.status !== 401) {
        const errorContext = error.response.status >= 500 ? "Server error occurred" : "Client error occurred";
        if (captureException) {
          const correlationId = (_b = error.config) == null ? void 0 : _b.correlationId;
          captureException(errorContext, {
            data: {
              status: error.response.status,
              data: error.response.data,
              originalError: error,
              ...correlationId && { correlationId }
            }
          });
        }
      }
      return Promise.reject(error);
    }
  };
};
var createCorrelationIdInterceptor = () => {
  return {
    onFulfilled: (response) => {
      const correlationId = Object.keys(response.headers).reduce((acc, key) => {
        if (key.toLowerCase() === "x-correlation-id") {
          const value = response.headers[key];
          return typeof value === "string" && value.trim() ? value.trim() : acc;
        }
        return acc;
      }, void 0);
      if (correlationId && response.config) {
        response.config.correlationId = correlationId;
      }
      return response;
    },
    onRejected: (error) => {
      var _a;
      if ((_a = error.response) == null ? void 0 : _a.headers) {
        const correlationId = Object.keys(error.response.headers).reduce((acc, key) => {
          if (key.toLowerCase() === "x-correlation-id") {
            const value = error.response.headers[key];
            return typeof value === "string" && value.trim() ? value.trim() : acc;
          }
          return acc;
        }, void 0);
        if (correlationId && error.config) {
          error.config.correlationId = correlationId;
        }
      }
      return Promise.reject(error);
    }
  };
};
var applyIgniteInterceptors = (axiosInstance, options = {}) => {
  const { getAuthToken, captureException, logoutAction } = options;
  if (getAuthToken) {
    const authInterceptor = createAuthRequestInterceptor({ getAuthToken, captureException });
    axiosInstance.interceptors.request.use(authInterceptor.onFulfilled, authInterceptor.onRejected);
  }
  const auth401Interceptor = create401ResponseInterceptor({ logoutAction });
  axiosInstance.interceptors.response.use(auth401Interceptor.onFulfilled, auth401Interceptor.onRejected);
  const errorInterceptor = createErrorResponseInterceptor({ captureException });
  axiosInstance.interceptors.response.use(errorInterceptor.onFulfilled, errorInterceptor.onRejected);
  return axiosInstance;
};
var createIgniteAxiosClientInstance = (options) => {
  const {
    baseURL,
    withCredentials = true,
    defaultHeaders = {
      "Content-Type": "application/json",
      Accept: "application/json"
    }
  } = options;
  if (!baseURL || baseURL.trim() === "") {
    throw new Error("Base URL is required to create an IgniteAxiosClient instance");
  }
  const axiosInstance = axios.create({
    baseURL,
    withCredentials,
    headers: defaultHeaders
  });
  return axiosInstance;
};
var createIgniteAxiosClient = (options) => {
  const { instance, timeout = 3e4 } = options;
  return (config) => {
    if (!config.timeout) {
      config.timeout = timeout;
    }
    return instance.request(config).then((response) => response.data).catch((error) => {
      var _a;
      if (error.response) {
        const correlationId = (_a = error.config) == null ? void 0 : _a.correlationId;
        throw {
          status: error.response.status,
          data: error.response.data,
          originalError: error,
          ...correlationId && { correlationId }
        };
      }
      throw error;
    });
  };
};
export {
  applyIgniteInterceptors,
  create401ResponseInterceptor,
  createAuthRequestInterceptor,
  createCorrelationIdInterceptor,
  createErrorResponseInterceptor,
  createIgniteAxiosClient,
  createIgniteAxiosClientInstance
};
//# sourceMappingURL=index.js.map